import { reactive } from 'vue'

import { resolveFormula } from '@baserow/modules/core/formula'
import RuntimeFormulaContext from '@baserow/modules/core/runtimeFormulaContext'

import { notifyIf } from '@baserow/modules/core/utils/error'
// Recorded for a table whose fields could not be fetched. Kept apart from an
// empty list, which is a table that really has no fields, and from an absent
// entry, which is a table nothing has fetched yet.
export const FIELDS_UNAVAILABLE = Symbol('fieldsUnavailable')

/**
 * How long a workflow button stays disabled on a row after a click of it was
 * accepted.
 *
 * This mirrors `DATABASE_BUTTON_DISPATCH_LOCK_TTL_SECONDS`, the TTL on the
 * per (field_id, row_id) dispatch lock (ADR 006 section 3), and mirroring is
 * the entire point: for exactly that window the server refuses a second click
 * on this (field, row) pair, so a button offered as clickable would be lying.
 * Keeping the two in step means the UI never presents an action that would
 * come straight back as a 409.
 *
 * The two drifting apart degrades safely in both directions, which is why this
 * is a mirrored constant and not a value fetched from the backend: too short
 * and the server still rejects the extra click, it just costs a round trip to
 * say so; too long and the button stays disabled a little longer than it
 * strictly had to. Neither lets a duplicate run through.
 */
export const BUTTON_FIELD_CLICK_COOLDOWN_MS = 10000

/**
 * The (field, row) pairs whose workflow was started recently enough that the
 * backend would still reject another click, mapped to the timeout clearing
 * them.
 *
 * Module-level, and therefore shared by every component that renders a button
 * field, because the same button on the same row is routinely on screen more
 * than once - a grid cell and an open row edit modal, two grid views side by
 * side - and each of those is a separate component instance with its own
 * `data`. Component-local state would disable the copy that was clicked and
 * leave its twin looking ready to go. Keying by field and row rather than by
 * component also means the disabled state survives the grid virtualising the
 * cell away and remounting it on scroll, which component-local state would
 * silently reset.
 */
const recentClicksByFieldAndRow = reactive(new Map())

function recentClickKey(fieldId, rowId) {
  return `${fieldId}:${rowId}`
}

/**
 * Marks a (field, row) pair as just-clicked, and schedules the mark to expire.
 *
 * Re-marking an already-marked pair restarts the window rather than stacking a
 * second timeout, so the mark always clears exactly one cooldown after the most
 * recent click instead of leaving an orphaned timer to clear it early.
 */

/**
 * Marks a (field, row) pair as just-clicked, and schedules the mark to expire.
 *
 * Re-marking an already-marked pair restarts the window rather than stacking a
 * second timeout, so the mark always clears exactly one cooldown after the most
 * recent click instead of leaving an orphaned timer to clear it early.
 */
export function markButtonFieldClicked(fieldId, rowId) {
  const key = recentClickKey(fieldId, rowId)
  clearTimeout(recentClicksByFieldAndRow.get(key))
  recentClicksByFieldAndRow.set(
    key,
    setTimeout(() => {
      recentClicksByFieldAndRow.delete(key)
    }, BUTTON_FIELD_CLICK_COOLDOWN_MS)
  )
}

export function isButtonFieldRecentlyClicked(fieldId, rowId) {
  return recentClicksByFieldAndRow.has(recentClickKey(fieldId, rowId))
}

/**
 * Test seam: drops every outstanding mark and its timer.
 *
 * Only for tests, which would otherwise leak marks (and pending timeouts) from
 * one case into the next through this deliberately module-level state.
 */

/**
 * Test seam: drops every outstanding mark and its timer.
 *
 * Only for tests, which would otherwise leak marks (and pending timeouts) from
 * one case into the next through this deliberately module-level state.
 */
export function clearButtonFieldRecentClicks() {
  recentClicksByFieldAndRow.forEach((timeout) => clearTimeout(timeout))
  recentClicksByFieldAndRow.clear()
}

/**
 * Resolves one of a button field's formulas against a row, client-side.
 *
 * Shared by both of them - `confirm_formula` and `disabled_formula` - so that
 * a formula means the same thing wherever it is used, and in particular so
 * both see the identical row context and can reference the row's fields the
 * same way.
 *
 * Returns the resolved text, or an empty string when the formula is empty or
 * cannot be resolved.
 */

/**
 * Resolves one of a button field's formulas against a row, client-side.
 *
 * Shared by both of them - `confirm_formula` and `disabled_formula` - so that
 * a formula means the same thing wherever it is used, and in particular so
 * both see the identical row context and can reference the row's fields the
 * same way.
 *
 * Returns the resolved text, or an empty string when the formula is empty or
 * cannot be resolved.
 */
export function resolveButtonFormula($registry, formulaObject, row, fields) {
  if (!formulaObject?.formula) {
    return ''
  }
  const formulaFunctions = {
    get: (name) => $registry.get('runtimeFormulaFunction', name),
  }
  const runtimeFormulaContext = new Proxy(
    new RuntimeFormulaContext($registry.getAll('databaseDataProvider'), {
      row,
      fields,
    }),
    {
      get(target, prop) {
        return target.get(prop)
      },
    }
  )
  const result = resolveFormula(
    formulaObject,
    formulaFunctions,
    runtimeFormulaContext
  )
  if (result === null || result === undefined) {
    return ''
  }
  return `${result}`.trim()
}

/**
 * Whether a resolved `disabled_formula` should be read as "yes, disable".
 *
 * Baserow's runtime formulas resolve to text, so plain JavaScript truthiness
 * is the wrong test: the string `"false"` and the string `"0"` are both
 * truthy to JavaScript, and a user writing a formula that produces either of
 * them plainly means "not disabled". `""` - what an empty cell resolves to,
 * and the common case for `get('fields.field_1')` on a row where the action
 * has not happened yet - has to be falsy for the whole feature to work.
 *
 * Erring towards "disabled" for anything unrecognised is deliberate: the
 * failure this guards against is an irreversible duplicate action, so an
 * ambiguous condition should stop the click rather than wave it through.
 */
export function isButtonFormulaValueTruthy(value) {
  if (value === null || value === undefined) {
    return false
  }
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'number') {
    return value !== 0
  }
  const text = `${value}`.trim().toLowerCase()
  return !['', '0', 'false'].includes(text)
}

/**
 * Percent-encodes the whitespace in a resolved URL. Row values often contain
 * spaces, which our URL validation rejects. Only whitespace is touched, or we
 * would mangle or double-encode the rest.
 */
export function encodeUrlWhitespace(url) {
  return url.replace(/\s/g, (character) => encodeURIComponent(character))
}

/**
 * Mirrors the builder's `ALLOWED_LINK_PROTOCOLS`, duplicated so this module
 * does not depend on the builder module.
 */
export const ALLOWED_BUTTON_URL_PROTOCOLS = [
  'ftp:',
  'ftps:',
  'ftpes:',
  'http:',
  'https:',
  'mailto:',
  'sftp:',
  'sms:',
  'tel:',
]

/**
 * Only used so a relative URL parses at all. Its protocol is an allowed one,
 * so relative URLs keep passing through.
 */
const RELATIVE_URL_BASE = 'http://baserow.invalid'

/**
 * Returns the URL when its protocol is allowed and an empty string when it is
 * not, which is what keeps a formula-built `javascript:` URL from being
 * navigated to. A URL without a protocol is relative and passes through.
 *
 * The protocol comes from `new URL()`, the same parse the browser runs, rather
 * than a regex: the parser strips leading C0 controls first, so a regex would
 * read `\x01javascript:` as relative while the browser still runs it.
 *
 * The original string is returned, since parsing re-encodes what the formula
 * deliberately built.
 */
export function urlWithAllowedProtocol(url) {
  let protocol
  try {
    protocol = new URL(url, RELATIVE_URL_BASE).protocol
  } catch (error) {
    // Not something the browser could navigate to either.
    return ''
  }
  return ALLOWED_BUTTON_URL_PROTOCOLS.includes(protocol.toLowerCase())
    ? url
    : ''
}

// Keyed by store, not by application id alone: one server process renders
// every request, and a promise closed over one request's store must not be
// handed to another's.
const inFlightIntegrations = new WeakMap()

/**
 * Fetches a database's integrations, at most once per database. A database has
 * no single entry point the way a builder does, so the first editor to need
 * them asks. Loading is remembered on the application, so a refetch that
 * replaces it asks again, and a failure is reported here rather than by the
 * caller, since several callers can await one request.
 *
 * @param {Object} store The Vuex store.
 * @param {Number} applicationId The database whose integrations are wanted.
 * @return {Promise<Boolean>} Whether the list was loaded.
 */
export function fetchIntegrationsOnce(store, applicationId) {
  const current = () => store.getters['application/get'](applicationId)
  if (current()?._integrationsLoadedOnce) {
    return Promise.resolve(true)
  }
  if (!inFlightIntegrations.has(store)) {
    inFlightIntegrations.set(store, new Map())
  }
  const inFlight = inFlightIntegrations.get(store)
  if (!inFlight.has(applicationId)) {
    const request = (async () => {
      // Resolved on both sides of the await: `forceSetAll` replaces the
      // object, and filling one while marking another leaves both wrong.
      const application = current()
      if (!application) {
        return false
      }
      let loaded
      try {
        loaded = await store.dispatch('integration/fetch', { application })
      } catch (error) {
        // Once for the request, whoever is waiting on it.
        notifyIf(error, 'application')
        return false
      }
      // Backed off because the list changed under it, so what is there now is
      // not the whole list. Left unmarked, so the next ask fetches again.
      if (loaded === null) {
        return false
      }
      const settled = current()
      if (!settled) {
        return false
      }
      await store.dispatch('application/forceUpdate', {
        application: settled,
        data: { _integrationsLoadedOnce: true },
      })
      return true
    })()
    // Dropped once it settles, so a failed one is asked again.
    inFlight.set(
      applicationId,
      request.finally(() => inFlight.delete(applicationId))
    )
  }
  return inFlight.get(applicationId)
}
